import type { Project } from '../../data/projects';
import { AppLink } from '../AppLink';
import { Icon } from '../Icon';
import { Picture } from '../Picture';

export const monographLabel = (n: number) => `Monograph ${String(n).padStart(2, '0')}`;

/** Known facts only, in display order. Unknown facts are simply left out. */
export function projectFacts(p: Project): Array<[label: string, value: string]> {
  const f = p.facts ?? {};
  const rows: Array<[string, string | undefined]> = [
    ['Scale', f.scale],
    ['Location', f.location],
    ['Completed', f.completed],
    ['Discipline', f.discipline],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1]));
}

type Props = {
  project: Project;
  /** Heading level of the card title (h2 on the portfolio index, h3 inside a titled section). */
  headingLevel?: 'h2' | 'h3';
  sizes?: string;
  className?: string;
};

/** Monograph card from the curated portfolio design. The whole card links to the case study. */
export function ProjectCard({
  project: p,
  headingLevel: Heading = 'h2',
  sizes = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw',
  className = '',
}: Props) {
  const facts = projectFacts(p);
  return (
    <article
      className={`group relative flex flex-col bg-surface-ivory rounded-xl overflow-hidden shadow-sm hover:shadow-xl focus-within:shadow-xl has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-gold-dark transition-all duration-500 hover:-translate-y-1 ${className}`}
    >
      <div className="relative w-full h-72 md:h-[380px] overflow-hidden bg-surface-container-high">
        <Picture
          name={p.image}
          alt={p.imageAlt}
          sizes={sizes}
          className="block w-full h-full"
          imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/75 via-botanical-dark/15 to-transparent" />
        <div className="absolute top-space-sm left-space-sm">
          <span className="inline-block px-space-sm py-space-xs rounded bg-surface-ivory/90 backdrop-blur-md font-label-sm text-label-sm uppercase text-primary tracking-widest shadow-sm">
            {p.tag}
          </span>
        </div>
        <div className="absolute bottom-space-sm left-space-sm right-space-sm flex justify-between items-end gap-space-sm text-on-primary">
          <div>
            <p className="font-label-sm text-label-sm text-gold-light uppercase tracking-wider mb-space-xs">
              {monographLabel(p.monograph)}
            </p>
            <Heading className="font-headline-sm text-headline-sm text-on-primary">
              <AppLink
                href={`/portfolio/${p.slug}`}
                className="after:absolute after:inset-0 after:z-10 focus-visible:outline-none"
              >
                {p.title}
              </AppLink>
            </Heading>
          </div>
          <Icon
            name="arrow_outward"
            size={24}
            className="text-gold-light group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
          />
        </div>
      </div>
      <div className="p-space-md flex flex-col gap-space-sm bg-surface-ivory flex-1">
        <p className="font-body-md text-body-md text-on-surface-variant">{p.summary}</p>
        {facts.length > 0 && (
          <dl className="mt-auto bg-surface-cream rounded p-space-sm grid grid-cols-2 gap-y-space-xs gap-x-space-sm font-label-sm text-label-sm">
            {facts.map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <dt className="text-on-surface-variant uppercase tracking-wider">{label}</dt>
                <dd className="text-primary font-semibold">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </article>
  );
}
